import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Grid2 as Grid,
  Chip,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { Invitation, InvitationStatus } from "../../models/Invitation";
import { invitationService } from "../../services/invitationService";
import useNotification from "../../hooks/useNotification";

interface InvitedParticipantsDialogProps {
  open: boolean;
  onClose: () => void;
  courseId: string;
}

const InvitedParticipantsDialog: React.FC<InvitedParticipantsDialogProps> = ({
  open,
  onClose,
  courseId,
}) => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { showNotification } = useNotification();

  useEffect(() => {
    if (open) {
      fetchInvitations();
    }
  }, [open, courseId]);

  const fetchInvitations = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response: [] = await invitationService.listInvitations(courseId);
      setInvitations(response ?? []);
    } catch (error) {
      console.error("Failed to fetch invitations:", error);
      setError("Failed to load invitations. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      const { message } = await invitationService.resendInvitation(
        courseId,
        invitationId
      );
      if (message) {
        showNotification(message, "success");
      }
      // Refresh the invitations list
      await fetchInvitations();
    } catch (error) {
      console.error("Failed to resend invitation:", error);
      setError("Failed to resend invitation. Please try again.");
      showNotification(
        "Failed to resend invitation. Please try again.",
        "error"
      );
    }
  };

  const handleResendAllInvitations = async () => {
    try {
      const { message } = await invitationService.resendAllInvitations(
        courseId
      );
      if (message) {
        showNotification(message, "success");
      }
      await fetchInvitations();
    } catch (error) {
      console.error("Failed to resend all invitations:", error);
      showNotification(
        "Failed to resend all invitations. Please try again.",
        "error"
      );
    }
  };

  const handleResendPendingInvitations = async () => {
    try {
      const { message } = await invitationService.resendPendingInvitations(
        courseId
      );
      if (message) {
        showNotification(message, "success");
      }
      await fetchInvitations();
    } catch (error) {
      console.error("Failed to resend pending invitations:", error);
      showNotification(
        "Failed to resend pending invitations. Please try again.",
        "error"
      );
    }
  };

  const pendingInvitations = invitations.filter(
    (invitation) =>
      !invitation.isAccepted && invitation.status !== InvitationStatus.REVOKED
  );

  const renderContent = () => {
    if (isLoading) {
      return <Typography>Loading invitations...</Typography>;
    }

    if (error) {
      return <Typography color="error">{error}</Typography>;
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <TableContainer
          component={Paper}
          sx={{ flexGrow: 1, overflow: "auto", mb: 2 }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>Invitation Sent At</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.map((invitation) => (
                <TableRow key={invitation.id}>
                  <TableCell>{invitation.participant.email}</TableCell>
                  <TableCell>
                    {invitation.participant.firstName}{" "}
                    {invitation.participant.lastName}
                  </TableCell>
                  <TableCell>{invitation.participant.organization}</TableCell>
                  <TableCell>
                    {/*if invitationSentDate is null, show "Not sent in a pill"*/}
                    {invitation.invitationSentDate ? (
                      new Date(invitation.invitationSentDate).toLocaleString(
                        "en-US",
                        {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )
                    ) : (
                      <Chip label="Not sent" color="error" />
                    )}
                  </TableCell>
                  <TableCell>
                    {invitation.isAccepted ? (
                      <Chip label="Accepted" color="success" />
                    ) : (
                      <Chip label="Pending" color="error" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      startIcon={<FontAwesomeIcon icon={faEnvelope} />}
                      onClick={() =>
                        handleResendInvitation(invitation.id.toString())
                      }
                      disabled={
                        invitation.status === InvitationStatus.ACCEPTED ||
                        invitation.status === InvitationStatus.REVOKED
                      }
                    >
                      Resend
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid size={{ xs: 12, sm: "auto" }}>
            <Button
              variant="outlined"
              startIcon={<FontAwesomeIcon icon={faEnvelope} />}
              onClick={handleResendPendingInvitations}
              disabled={pendingInvitations.length === 0}
            >
              Send Unsent Invitations ({pendingInvitations.length})
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: "auto" }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<FontAwesomeIcon icon={faEnvelope} />}
              onClick={handleResendAllInvitations}
              disabled={invitations.length === 0}
            >
              Resend All Invitations ({invitations.length})
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: fullScreen ? "100%" : "90%",
          maxHeight: "none",
        },
      }}
    >
      <DialogTitle>Course Invitations</DialogTitle>
      <DialogContent>{renderContent()}</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvitedParticipantsDialog;
